import React, {useState} from 'react';
import {
    Avatar,
    Box,
    Button,
    ButtonGroup,
    IconButton,
    Link as MuiLink,
    Menu,
    MenuItem,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {Link as RouterLink} from 'react-router';
import {getEntityKBUrl, getWikidataUrl} from '../../../../../../utils/entityLinks';
import {resolveEntityAvatarUrl} from '../../../../../../utils/entityAvatarUrl';
import {TYPE_META, DEFAULT_META, getEntityTypeMeta as getMeta} from '../../../../../../utils/entityTypeMeta';
import {unlinkedMentionBorderStyle, useEntityDisplayConfig} from '../../../../../../hooks/entityDisplayConfig';

/**
 * Self-contained entity recognition suggestion card.
 * Receives action callbacks so it owns the full layout including buttons.
 */
export default function EntityRecognitionTaskCard({
    task,
    busy = false,
    disabledReason = null,
    canNavigate = false,
    onNavigate,
    onApply,
    onReject,
    onSelectAnotherEntity,
}) {
    const [anchorEl, setAnchorEl] = useState(null);
    const {unlinkedUnderlineEnabled} = useEntityDisplayConfig();
    const unlinkedBorder = unlinkedMentionBorderStyle(unlinkedUnderlineEnabled);

    if (!task?.entity) {
        return <Typography variant="body2" color="error">Missing entity data</Typography>;
    }

    const entity = task.entity;
    const kbUrl = entity.entityId ? getEntityKBUrl(entity.entityId) : null;
    const wikidataUrl = entity.id ? getWikidataUrl(entity.id) : null;
    const title = entity.label || entity.id || 'Unknown entity';
    const description = entity.description || null;
    const entityType = entity.entityType || task.entityType || null;
    const meta = getMeta(entityType);

    const imageUrl = resolveEntityAvatarUrl({
        wikidataId: entity.id,
        entityId: entity.entityId,
        imageUrl: entity.imageUrl || null,
    });

    const initials = imageUrl
        ? null
        : String(title).replace(/[^a-zA-Z0-9]/g, ' ').trim().split(/\s+/).slice(0, 2)
              .map(w => w[0]?.toUpperCase()).join('');

    const handleMenuOpen = (e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); };
    const handleMenuClose = () => setAnchorEl(null);
    const handleLinkAnother = (e) => { onSelectAnotherEntity?.(e); handleMenuClose(); };

    const AVATAR_SIZE = 48;

    return (
        <Box
            onClick={canNavigate ? () => onNavigate?.(task) : undefined}
            sx={{
                display: 'flex',
                borderRadius: 1,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                opacity: busy ? 0.65 : 1,
                transition: 'opacity 0.15s, box-shadow 0.15s',
                cursor: canNavigate ? 'pointer' : 'default',
                ...(canNavigate && {
                    '&:hover': {
                        boxShadow: `inset 0 0 0 1px ${meta.color}66`,
                        borderColor: `${meta.color}66`,
                    },
                }),
            }}
        >
            {/* Left accent strip — entity type color */}
            <Box sx={{width: 4, flexShrink: 0, bgcolor: meta.color}}/>

            {/* Card body: single row — avatar | text | Link button | ✕ */}
            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{flex: 1, minWidth: 0, p: '8px 8px 8px 10px'}}
            >
                <Avatar
                    variant="rounded"
                    src={imageUrl || undefined}
                    alt={title}
                    sx={{
                        width: AVATAR_SIZE,
                        height: AVATAR_SIZE,
                        flexShrink: 0,
                        fontSize: '1rem',
                        fontWeight: 700,
                        bgcolor: imageUrl ? 'transparent' : meta.bg,
                        color: meta.color,
                        border: `1px solid ${meta.color}44`,
                    }}
                >
                    {!imageUrl && (initials || title[0]?.toUpperCase())}
                </Avatar>

                {/* Text column: mention → entity, type badge, description */}
                <Stack sx={{flex: 1, minWidth: 0}}>
                    {/*
                      isLinked = entity has a Wikidata QID (kbUrl or wikidataUrl present)
                        → show: task.text (yellow underline) → entity name (blue dashed)
                      !isLinked = unlinked mention only
                        → show: entity name (yellow underline only, no blue)
                    */}
                    {(() => {
                        const isLinked = !!(kbUrl || wikidataUrl);

                        const entityNameSx = isLinked
                            ? {color: 'blue', borderBottom: '1px dashed blue', lineHeight: 1.25}
                            : {color: 'text.primary', borderBottom: unlinkedBorder, lineHeight: 1.25};

                        const sharedSpanSx = {
                            fontFamily: 'inherit',
                            fontSize: '0.875rem',
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            flexShrink: 1,
                            minWidth: 0,
                        };

                        const entityNode = kbUrl ? (
                            <MuiLink
                                component={RouterLink}
                                to={kbUrl}
                                variant="body2"
                                fontWeight={700}
                                underline="none"
                                noWrap
                                onClick={(e) => e.stopPropagation()}
                                sx={{...entityNameSx, display: 'inline', maxWidth: isLinked && task.text ? '55%' : '100%', flexShrink: 1, minWidth: 0, '&:hover': {bgcolor: 'rgba(25,118,210,0.10)'}}}
                            >
                                {title}
                            </MuiLink>
                        ) : wikidataUrl ? (
                            <MuiLink
                                href={wikidataUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="body2"
                                fontWeight={700}
                                underline="none"
                                noWrap
                                onClick={(e) => e.stopPropagation()}
                                sx={{...entityNameSx, display: 'inline', maxWidth: isLinked && task.text ? '55%' : '100%', flexShrink: 1, minWidth: 0, '&:hover': {bgcolor: 'rgba(25,118,210,0.10)'}}}
                            >
                                {title}
                            </MuiLink>
                        ) : (
                            <Box component="span" sx={{...sharedSpanSx, ...entityNameSx, maxWidth: '100%'}}>
                                {title}
                            </Box>
                        );

                        return (
                            <Box sx={{display: 'flex', alignItems: 'baseline', gap: 0.5, width: 'fit-content', maxWidth: '100%', overflow: 'hidden'}}>
                                {/* Show mention text with yellow underline only when entity is linked */}
                                {isLinked && task.text && (
                                    <Box component="span" sx={{...sharedSpanSx, color: 'text.primary', borderBottom: unlinkedBorder, maxWidth: '45%'}}>
                                        {task.text}
                                    </Box>
                                )}
                                {isLinked && task.text && (
                                    <Box component="span" sx={{color: 'text.disabled', fontSize: '0.875rem', lineHeight: 1.25, flexShrink: 0}}>
                                        →
                                    </Box>
                                )}
                                {entityNode}
                            </Box>
                        );
                    })()}

                    {meta.label && (
                        <Typography
                            component="span"
                            sx={{
                                fontSize: '0.6rem',
                                fontWeight: 700,
                                letterSpacing: '0.06em',
                                textTransform: 'uppercase',
                                color: meta.color,
                                lineHeight: 1.3,
                                opacity: 0.85,
                            }}
                        >
                            {meta.label}
                        </Typography>
                    )}

                    {description && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                lineHeight: 1.35,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                mt: 0.5,
                            }}
                        >
                            {description}
                        </Typography>
                    )}
                </Stack>

                {/* Link button group — vertically centered with text */}
                <Stack direction="row" alignItems="center" flexShrink={0} onClick={(e) => e.stopPropagation()}>
                    <ButtonGroup variant="contained" size="small" disableElevation>
                        <Button
                            startIcon={<LinkIcon sx={{fontSize: '12px !important'}}/>}
                            onClick={(e) => { e.stopPropagation(); onApply?.(e); }}
                            disabled={busy || !!disabledReason}
                            sx={{
                                textTransform: 'none',
                                fontSize: '0.72rem',
                                py: 0.25,
                                px: 1,
                                bgcolor: meta.color,
                                '&:hover': {bgcolor: meta.color, filter: 'brightness(0.9)'},
                                '&.Mui-disabled': {bgcolor: 'action.disabledBackground'},
                            }}
                        >
                            Link
                        </Button>
                        <Button
                            size="small"
                            onClick={handleMenuOpen}
                            disabled={busy || !!disabledReason}
                            sx={{
                                px: 0.5,
                                minWidth: 'auto',
                                bgcolor: meta.color,
                                '&:hover': {bgcolor: meta.color, filter: 'brightness(0.9)'},
                                '&.Mui-disabled': {bgcolor: 'action.disabledBackground'},
                            }}
                        >
                            <ArrowDropDownIcon sx={{fontSize: 16}}/>
                        </Button>
                    </ButtonGroup>

                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                        anchorOrigin={{vertical: 'top', horizontal: 'right'}}
                        transformOrigin={{vertical: 'bottom', horizontal: 'right'}}
                        slotProps={{paper: {elevation: 2}}}
                    >
                        <MenuItem dense onClick={handleLinkAnother} sx={{fontSize: '0.8rem'}}>
                            Link a different entity…
                        </MenuItem>
                    </Menu>
                </Stack>

                {/* Reject icon */}
                <Tooltip title="Skip this suggestion" placement="top">
                    <span>
                        <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); onReject?.(e); }}
                            disabled={busy || !!disabledReason}
                            sx={{
                                color: 'text.disabled',
                                p: 0.25,
                                flexShrink: 0,
                                '&:hover': {color: 'error.main', bgcolor: 'error.50'},
                            }}
                        >
                            <CloseIcon sx={{fontSize: 14}}/>
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>
        </Box>
    );
}
